import { RoutesConstants } from '@/main/routes/routesConstants';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import {
  bodyshopApi,
  manufacturerApi,
  modelApi,
  quoteApi,
  subModelApi,
  yearApi
} from '@/main/api';
import { KeyValue } from '@/main/models/common';
import { InsurerFilterRequest, PreQuoteRequest } from '@/main/models/request';
import { BodyshopRegistrationDataResponse, PreQuoteResponse } from '@/main/models/response';
import { HeadQuarterBranchFilterEnum } from '@/main/services/enum/HeadQuarterBranchFilterEnum';
import { StatusRegisterEnum } from '@/main/services/enum/StatusRegisterEnum';
import { getDefaultCountryOptionDrop, getDefaultOptionDrop, getTranslateByKey, isNullUndefined, removeEmptyValues } from '@/main/services/utils';
import { userIsInsurer } from '@/main/services/utilsUser';
import { getCountriesUser, useCountryReducer } from '@/main/store/country';
import { getInsurers, useInsurerReducer } from '@/main/store/insurer';
import { FormProvider, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';

interface IPreQuoteProvider {
  children: React.ReactNode;
}

interface IPreQuoteContext {
  openModalCancel: boolean;
  isLoadingInfoOrder: boolean;
  isLoadingVehicleManufacturer: boolean;
  isLoadingVehicleModel: boolean;
  isLoadingVehicleYear: boolean;
  isLoadingSubModel: boolean;
  isLoadingBodyshop: boolean;
  setOpenModalCancel: (value: boolean) => void;
  displayModalCancel: () => void;
  guidQuote: string | null;
  preQuoteData: PreQuoteResponse | null;
  bodyshopData: BodyshopRegistrationDataResponse | null;
  vehicleManufacturers: KeyValue[] | null;
  vehicleModels: KeyValue[] | null;
  vehicleYears: KeyValue[] | null;
  loadVehicleManufacurer: (
    vehicleModelSelected: KeyValue | null,
    vehicleSubModelSelected: KeyValue | null,
    yearSelected: KeyValue | null
  ) => void;
  loadVehicleModel: (
    vehicleManufacturerSelected: KeyValue | null,
    vehicleSubModelSelected: KeyValue | null,
    yearSelected: KeyValue | null
  ) => void;
  loadVehicleYears: (
    vehicleManufacturerSelected: KeyValue | null,
    vehicleModelSelected: KeyValue | null,
    subModelSelected: KeyValue | null
  ) => void;
  subModels: KeyValue[] | null;
  loadSubModel: (
    vehicleManufacturerSelected: KeyValue | null,
    vehicleModelSelected: KeyValue | null,
    yearSelected: KeyValue | null
  ) => void;
}

const INTIAL_FORM_STATE_GLASS = {
  guidQuoteItem: '',
  partnumber: '',
  code: '',
  refPrice: 0 as number,
  notes: '' as string,
};
export type IPreQuoteFormGlass = typeof INTIAL_FORM_STATE_GLASS;
export type IKeysPreQuoteFormGlass = keyof IPreQuoteFormGlass;

const INITIAL_FORM_STATE_TIRE = {
  guidQuoteItem: '',
  partnumber: '',
  brand: null as KeyValue | null,
  model: '' as string,
  width: '' as string,
  height: '' as string,
  rim: '' as string,
  range: '' as string,
  refPrice: 0 as number,
  notes: '' as string,
};

export type IPreQuoteFormTire = typeof INITIAL_FORM_STATE_TIRE;
export type IKeysPreQuoteFormTire = keyof IPreQuoteFormTire;

const INITIAL_FORM_STATE = {
  status: null as KeyValue | null,

  // vehicle
  manufacturer: null as KeyValue | null,
  model: null as KeyValue | null,
  trim: null as KeyValue | null,
  year: null as KeyValue | null,

  // bodyshop
  country: null as KeyValue | null | undefined,
  companyName: '',
  rfc: '',
  tradeName: '',

  glasses: [] as IPreQuoteFormGlass[],
  tires: [] as IPreQuoteFormTire[],
};

export type IPreQuoteForm = typeof INITIAL_FORM_STATE;

const PreQuoteContext = createContext<IPreQuoteContext>({
  openModalCancel: false,
  isLoadingInfoOrder: false,
  isLoadingVehicleManufacturer: false,
  isLoadingVehicleModel: false,
  isLoadingBodyshop: false,
  isLoadingVehicleYear: false,
  isLoadingSubModel: false,
  setOpenModalCancel: () => { },
  displayModalCancel: () => { },
  guidQuote: '',
  preQuoteData: null,
  bodyshopData: null,
  vehicleManufacturers: null,
  vehicleModels: null,
  vehicleYears: null,
  loadVehicleManufacurer: (
    vehicleModelSelected: KeyValue | null,
    vehicleSubModelSelected: KeyValue | null,
    yearSelected: KeyValue | null
  ) => { },
  loadVehicleModel: (
    vehicleManufacturerSelected: KeyValue | null,
    vehicleSubModelSelected: KeyValue | null,
    yearSelected: KeyValue | null
  ) => { },
  loadVehicleYears: (
    vehicleManufacturerSelected: KeyValue | null,
    vehicleModelSelected: KeyValue | null,
    subModelSelected: KeyValue | null
  ) => { },
  subModels: null,
  loadSubModel: (
    vehicleManufacturerSelected: KeyValue | null,
    vehicleModelSelected: KeyValue | null,
    yearSelected: KeyValue | null
  ) => { },
});

function PreQuoteProvider({ children }: IPreQuoteProvider) {
  const methods = useForm<IPreQuoteForm>({
    mode: 'onChange',
    defaultValues: INITIAL_FORM_STATE
  });
  const [searchParams, setSearchParams] = useSearchParams();
  const guidQuote = searchParams.get('guidQuote');
  const [openModalCancel, setOpenModalCancel] = useState<boolean>(false);
  const [isLoadingInfoOrder, setIsLoadingInfoOrder] = useState(false);
  const [isLoadingBodyshop, setIsLoadingBodyshop] = useState(false);
  const [
    isLoadingVehicleManufacturer,
    setIsLoadingVehicleManufacturer
  ] = useState(false);
  const [isLoadingVehicleModel, setIsLoadingVehicleModel] = useState(false);
  const [isLoadingVehicleYear, setIsLoadingVehicleYear] = useState(false);
  const [isLoadingSubModel, setIsLoadingSubModel] = useState(false);
  const [preQuoteData, setPreQuoteData] = useState<PreQuoteResponse | null>(
    null
  );
  const [bodyshopData, setBodyshopData] = useState<BodyshopRegistrationDataResponse | null>(
    null
  );
  const [vehicleManufacturers, setVehicleManufacturers] = useState<
    KeyValue[] | null
  >(null);
  const [vehicleModels, setVehicleModels] = useState<KeyValue[] | null>(null);
  const [vehicleYears, setVehicleYears] = useState<KeyValue[] | null>(null);
  const [subModels, setSubModels] = useState<KeyValue[] | null>(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { countriesUser: countryReducer } = useCountryReducer();

  const optionDefaultCountryDrop = getDefaultCountryOptionDrop(
    countryReducer.data
  );

  function displayModalCancel() {
    setOpenModalCancel(true);
  }

  function loadInfoBodyshop(guidBodyshop: string) {
    setIsLoadingBodyshop(true);
    bodyshopApi
      .getRegistrationDataBodyshop({ guidBodyShop: guidBodyshop })
      .then(response => {
        const resultData = response.data.result;
        setBodyshopData(resultData);
      }).finally(() => {
        setIsLoadingBodyshop(false);
      });
  }

  async function loadInfoOrder() {
    if (guidQuote) {
      setIsLoadingInfoOrder(true);
      try {
        const { data: { result } } = await quoteApi.getPreQuote(guidQuote)
        setIsLoadingInfoOrder(false);

        if (result) {
          setPreQuoteData(result);
          if (!isNullUndefined(result.guidBodyShop)) loadInfoBodyshop(result.guidBodyShop);

        } else {
          toast.error(getTranslateByKey('information.quoteNotFound', true));
        }
      } finally {
        setIsLoadingInfoOrder(false);
      }
    } else {
      navigate(RoutesConstants.notFound);
    }
  }

  function loadVehicleManufacurer(
    vehicleModelSelected: KeyValue | null,
    vehicleSubModelSelected: KeyValue | null,
    yearSelected: KeyValue | null
  ) {
    setIsLoadingVehicleManufacturer(true);
    const filter = removeEmptyValues({
      guidVehicleManufacturer: null,
      guidVehicleModel: String(vehicleModelSelected?.key),
      guidVehicleSubModel: String(vehicleSubModelSelected?.key),
      guidVehicleYear: String(yearSelected?.key)
    });

    manufacturerApi
      .getVehicleManufacturerByFilters(filter)
      .then(response => {
        const { result } = response.data;
        if (result) {
          setVehicleManufacturers(result);
        } else {
          setVehicleManufacturers(null);
        }
      })
      .finally(() => {
        setIsLoadingVehicleManufacturer(false);
      });
  }

  function loadVehicleModel(
    vehicleManufacturerSelected: KeyValue | null,
    vehicleSubModelSelected: KeyValue | null,
    yearSelected: KeyValue | null
  ) {
    if (vehicleManufacturerSelected?.key) {
      setIsLoadingVehicleModel(true);
      const filter = removeEmptyValues({
        guidVehicleManufacturer: String(vehicleManufacturerSelected?.key),
        guidVehicleModel: null,
        guidVehicleSubModel: String(vehicleSubModelSelected?.key),
        guidVehicleYear: String(yearSelected?.key)
      });

      modelApi
        .getVehicleModelByFilters(filter)
        .then(response => {
          const { result } = response.data;
          if (result) {
            setVehicleModels(result);
          } else {
            setVehicleModels(null);
          }
        })
        .finally(() => {
          setIsLoadingVehicleModel(false);
        });
    }
  }

  function loadVehicleYears(
    vehicleManufacturerSelected: KeyValue | null,
    vehicleModelSelected: KeyValue | null,
    subModelSelected: KeyValue | null
  ) {
    const isAllEmpty = isNullUndefined(vehicleManufacturerSelected?.key) &&
      isNullUndefined(vehicleModelSelected?.key) &&
      isNullUndefined(subModelSelected?.key);

    if (vehicleManufacturerSelected?.key && vehicleModelSelected?.key) {
      setIsLoadingVehicleYear(true);
      const filter = removeEmptyValues({
        guidManufacturer: String(vehicleManufacturerSelected?.key),
        guidModel: String(vehicleModelSelected?.key),
        guidSubModel: String(subModelSelected?.key)
      });

      yearApi
        .getVehicleYearByManufacturerAndModel(filter)
        .then(response => {
          const { result } = response.data;
          if (result) {
            setVehicleYears(result);
          } else {
            setVehicleYears(null);
          }
        })
        .finally(() => {
          setIsLoadingVehicleYear(false);
        });
    }

    if (isAllEmpty) {
      setIsLoadingVehicleYear(true);
      setVehicleModels(null);
      setSubModels(null);
      yearApi
        .getVehicleYear()
        .then(response => {
          const { result } = response.data;
          if (result) {
            setVehicleYears(result);
          } else {
            setVehicleYears(null);
          }
        })
        .finally(() => {
          setIsLoadingVehicleYear(false);
        });

    }
  }

  function loadSubModel(
    vehicleManufacturerSelected: KeyValue | null,
    vehicleModelSelected: KeyValue | null,
    yearSelected: KeyValue | null
  ) {
    if (vehicleManufacturerSelected?.key && vehicleModelSelected?.key) {
      setIsLoadingSubModel(true);
      const filter = removeEmptyValues({
        guidManufacturer: String(vehicleManufacturerSelected?.key),
        guidModel: String(vehicleModelSelected?.key),
        guidYear: String(yearSelected?.key)
      });

      subModelApi
        .getVehicleSubModel(filter)
        .then(response => {
          const { result } = response.data;
          if (result) {
            setSubModels(result);
          } else {
            setSubModels(null);
          }
        })
        .finally(() => {
          setIsLoadingSubModel(false);
        });
    }
  }


  useEffect(() => {
    loadInfoOrder();
    if (isNullUndefined(countryReducer.data)) dispatch(getCountriesUser());
  }, []);

  useEffect(() => {
    if (!isNullUndefined(optionDefaultCountryDrop)) {
      const request: InsurerFilterRequest = {
        guidCountry: String(optionDefaultCountryDrop?.key),
        type: HeadQuarterBranchFilterEnum.All,
        idStatusRegister: StatusRegisterEnum.Active
      };

      dispatch(getInsurers(request));
    }
  }, [optionDefaultCountryDrop?.key]);

  return (
    <FormProvider {...methods}>
      <PreQuoteContext.Provider
        value={{
          guidQuote,
          displayModalCancel,
          openModalCancel,
          isLoadingInfoOrder,
          setOpenModalCancel,
          preQuoteData,
          bodyshopData,
          vehicleManufacturers,
          vehicleModels,
          vehicleYears,
          loadVehicleYears,
          subModels,
          loadSubModel,
          loadVehicleManufacurer,
          loadVehicleModel,
          isLoadingVehicleManufacturer,
          isLoadingVehicleModel,
          isLoadingVehicleYear,
          isLoadingSubModel,
          isLoadingBodyshop
        }}>
        {children}
      </PreQuoteContext.Provider>
    </FormProvider>
  );
}

const usePreQuoteContext = () => useContext(PreQuoteContext);

export { PreQuoteProvider, usePreQuoteContext };
